import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import Spinner from '../../components/Spinner';
import RequisiteWaitSpinner from '../../components/RequisiteWaitSpinner';
import VariantPage from '../../pages/VariantPage/VariantPage';
import Header from '../../components/organisms/Header/Header';
import A2APage from '../../pages/A2APage/A2APage';
import QrPage from '../../pages/QrPage/QrPage';
import SbpPage from '../../pages/SbpPage/SbpPage';
import CryptoPage from '../../pages/CryptoPage/CryptoPage';
import P2PPage from '../../pages/P2PPage/P2PPage';
import CancellationPage from '../../pages/CancellationPage/CancellationPage';
import UploadPage from '../../pages/UploadPage/UploadPage';
import Success from '../../pages/SuccessPage/Success';
import ReceiptUnconfirmed from '../../pages/ReceiptUnconfirmedPage/ReceiptUnconfirmed';
import PaymentExpired from '../../pages/PaymentExpiredPage/PaymentExpired';
import Error from '../../pages/ErrorPage/Error';
import LeaveChoiceModal from '../../components/organisms/LeaveChoiceModal/LeaveChoiceModal';
import LowTimeModal from '../../components/organisms/LowTimeModal/LowTimeModal';
import { TemplateProps } from '../types';
import { supportTarget } from '../../services/supportUrl';

const WrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DefaultTemplate = ({
  transaction: transactionRaw,
  loading,
  error,
  timeLeft,
  timeoutScreen,
  uuid,
  showConfirm,
  transgranError,
  receiptIntent,
  lowTimeWarning,
  onExtendTime,
  onDismissLowTime,
  onConfirm,
  onCancel,
  onDeclineOffer,
  onLeave,
  onRedirect,
  onSelectVariant,
  variantSubmitting,
  onRetrySearch,
  retrySearching,
  setRetry,
  setShowConfirm,
  setTransgranError,
  setLoading,
  formatTime,
}: TemplateProps) => {
  const transaction = transactionRaw as any;

  // CPAYMENT-1616: перезаливка чека в гейте заменяет файл на бэкенде и НЕ подтверждается повторно
  // (тикет уже не в PAYMENT_PENDING — второй confirm вернул бы 422). Мост до следующего полинга,
  // чтобы не мигал устаревший «failed» после успешной загрузки. Хуки — строго до ранних return.
  const [reuploading, setReuploading] = useState(false);

  // CPAYMENT-1730: на тикете, где клиент уже отметил «Я оплатил», «Отмена» на экране реквизита
  // сначала спрашивает, чего он хочет — уйти к мерчанту или всё-таки отменить заявку. Гейт стоит
  // здесь, в одной точке, а не в четырёх страницах методов оплаты.
  const [leaveChoice, setLeaveChoice] = useState(false);
  const onCancelClick = receiptIntent ? () => setLeaveChoice(true) : onCancel;

  useEffect(() => {
    if (transaction?.receiptVerification?.phase === 'pending') {
      setReuploading(false);
    }
  }, [transaction?.receiptVerification?.phase]);

  // CPAYMENT-1670: the client asked for a new requisite search — hold the ordinary search screen
  // until the ticket leaves the searching statuses, so neither the emptied variant tile nor the
  // "Не удалось найти реквизит" screen flashes between cascade rounds. Checked before `loading`
  // so the re-drive of the next round does not swap the staged spinner for the plain one.
  if (retrySearching) {
    return <RequisiteWaitSpinner />;
  }

  if (loading) {
    return <Spinner />;
  }

  // CPAYMENT-1716: ожидание кончилось ничем — экран таймаута идёт ДО showConfirm. Иначе клиент
  // залипал на форме загрузки чека с уже мёртвым тикетом: ранний return по showConfirm стоял
  // выше проверки статуса, и пришедший EXPIRED ничего не менял.
  if (timeoutScreen && transaction) {
    return (
      <>
        <Header
          transaction={transaction}
          ticketId={uuid || ''}
          timeLeft={formatTime(timeLeft || 0)}
          disableTimer
        />
        {timeoutScreen === 'payment-expired' ? (
          <PaymentExpired onContactSupport={() => onRedirect(supportTarget(transaction))} />
        ) : (
          <ReceiptUnconfirmed onContactSupport={() => onRedirect(supportTarget(transaction))} />
        )}
      </>
    );
  }

  if (showConfirm) {
    return (
      <UploadPage
        transaction={transaction}
        ticketId={uuid || ''}
        timeLeft={formatTime(timeLeft || 0)}
        onConfirm={onConfirm}
        onBack={() => setShowConfirm(false)}
        uuid={uuid}
      />
    );
  }

  if (
    transaction?.paymentAdapterSearchAttemptsCounter > 0 &&
    !transaction?.requisite?.type &&
    transaction?.status === 'CLIENT_PENDING'
  ) {
    if (transgranError) {
      return (
        <>
          <Header
            transaction={transaction}
            ticketId={uuid || ''}
            timeLeft={formatTime(timeLeft || 0)}
            disableTimer
          />
          <Error
            transaction={transaction}
            transgranError={transgranError}
            onOk={() => onRedirect(transaction.redirectUrl)}
            setTransgranError={setTransgranError}
            setLoading={setLoading}
          />
        </>
      );
    }

    return (
      <>
        <Header
          transaction={transaction}
          ticketId={uuid || ''}
          timeLeft={formatTime(timeLeft || 0)}
          disableTimer
        />
        <Error
          setRetry={setRetry}
          onCancel={onCancel}
          transaction={transaction}
          uuid={uuid}
          setTransgranError={setTransgranError}
          setLoading={setLoading}
        />
      </>
    );
  }

  if (
    transaction?.paymentAdapterSearchAttemptsCounter === 0 &&
    !transaction?.requisite?.type &&
    transaction?.status === 'CLIENT_PENDING'
  ) {
    return (
      <>
        <Header
          transaction={transaction}
          ticketId={uuid || ''}
          timeLeft={formatTime(timeLeft || 0)}
          disableTimer
        />
        <Error
          transaction={transaction}
          onCancel={() => onRedirect(transaction.redirectUrl)}
          setTransgranError={setTransgranError}
          setLoading={setLoading}
        />
      </>
    );
  }

  if (error) {
    return (
      <Error
        onOk={() => onRedirect(transaction?.redirectUrl)}
        setTransgranError={setTransgranError}
        setLoading={setLoading}
      />
    );
  }

  if (!transaction) {
    return (
      <Error
        onOk={() => onRedirect(transaction?.redirectUrl)}
        setTransgranError={setTransgranError}
        setLoading={setLoading}
      />
    );
  }

  if (transaction?.status === 'WAITING_FOR_VARIANT_SELECTION') {
    return (
      <VariantPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formatTime(timeLeft || 0)}
        onCancel={() => onDeclineOffer('variants')}
        onSelectVariant={onSelectVariant}
        variantSubmitting={variantSubmitting}
        onRetrySearch={onRetrySearch}
        retrySearching={retrySearching}
      />
    );
  }

  if (transaction?.status === 'WAITING_FOR_REQUISITE') {
    return <RequisiteWaitSpinner />;
  }

  if (transaction?.status === 'DECLINED' || transaction?.status === 'EXPIRED') {
    return (
      <CancellationPage
        transaction={transaction}
        timeLeft={formatTime(timeLeft || 0)}
        onConfirm={() => onRedirect(transaction.redirectUrl)}
        amount={transaction.amount}
        currency={transaction.requisite?.currency || transaction.currency}
        merchantName={transaction.merchant}
        paymentMethod={transaction.paymentMethod}
        ticketId={uuid || ''}
        setRetry={setRetry}
        uuid={uuid}
        setTransgranError={setTransgranError}
        setLoading={setLoading}
      />
    );
  }

  // CPAYMENT-1616: приём чека ещё не подтверждён — показывать «Платёж получен» нельзя.
  // Проверка идёт → нейтральное ожидание; документ не принят → снова экран загрузки.
  if (transaction.status === 'FILES_AUTO_VERIFICATION') {
    const rv = transaction.receiptVerification;

    if (rv?.phase === 'failed' && rv.canReupload && !reuploading) {
      return (
        <UploadPage
          transaction={transaction}
          ticketId={uuid || ''}
          timeLeft={formatTime(timeLeft || 0)}
          onConfirm={() => setReuploading(true)}
          onBack={() => onRedirect(transaction.redirectUrl)}
          uuid={uuid}
          reuploadNotice='Не удалось принять документ. Загрузите файл ещё раз'
        />
      );
    }

    // Ожидание — тот же экран загрузки, но блок с файлом заменён спиннером проверки.
    return (
      <UploadPage
        transaction={transaction}
        ticketId={uuid || ''}
        timeLeft={formatTime(timeLeft || 0)}
        onConfirm={onConfirm}
        onBack={() => onRedirect(transaction.redirectUrl)}
        uuid={uuid}
        verifying
      />
    );
  }

  // CPAYMENT-1616: чек так и не приняли (исчерпаны попытки / не дождались ответа проверки) —
  // экран успеха соврал бы. Даём понятный следующий шаг вместо кнопки «вернуться».
  if (
    transaction.status === 'FILES_VERIFICATION' &&
    transaction.receiptVerification?.phase === 'unconfirmed'
  ) {
    return (
      <>
        <Header
          transaction={transaction}
          ticketId={uuid || ''}
          timeLeft={formatTime(timeLeft || 0)}
        />
        <ReceiptUnconfirmed
          onContactSupport={() => onRedirect(supportTarget(transaction))}
        />
      </>
    );
  }

  if (
    transaction.status === 'COMPLETED' ||
    transaction.status === 'FILES_VERIFICATION'
  ) {
    return (
      <>
        <Header
          transaction={transaction}
          ticketId={uuid || ''}
          timeLeft={formatTime(timeLeft || 0)}
        />
        <Success
          transaction={transaction}
          onRedirect={() => onRedirect(transaction.redirectUrl)}
        />
      </>
    );
  }

  if (
    transaction?.requisite?.type === 'redirect_url' &&
    transaction?.requisite?.paymentUrl
  ) {
    return <Spinner description='Подготовка страницы оплаты...' />;
  }

  return (
    <>
      {loading && transaction.status !== 'COMPLETED' ? (
        <Spinner />
      ) : (
        <WrapperDiv>
          {(transaction.status === 'WAITING_FOR_SMS' ||
            transaction.status === 'PAYMENT_PENDING') && (
            <>
              {transaction.paymentMethod === 'CRYPTO' && (
                <CryptoPage
                  transaction={transaction}
                  ticketId={uuid || ''}
                  onCancel={onCancelClick}
                  onDeclineOffer={onDeclineOffer}
                  onConfirm={onConfirm}
                  timeLeft={formatTime(timeLeft || 0)}
                  setShowConfirm={setShowConfirm}
                />
              )}
              {(transaction.paymentMethod === 'SBP P2P RUB' ||
                transaction.paymentMethod === 'TRANS SBP P2P RUB' ||
                transaction.paymentMethod === 'SBP MATCHING RUB' ||
                transaction.paymentMethod === 'OZON SBP RUB' ||
                transaction.paymentMethod === 'ALFA SBP RUB' ||
                transaction.paymentMethod === 'SBER SBP RUB' ||
                transaction.paymentMethod === 'VTB SBP RUB' ||
                transaction.paymentMethod === 'TBANK SBP RUB' ||
                transaction.paymentMethod === 'TBANK TRANS SBP P2P RUB' ||
                transaction.paymentMethod === 'MOBILE P2P KZT' ||
                transaction.paymentMethod === 'M10AZN') && (
                <SbpPage
                  transaction={transaction}
                  ticketId={uuid || ''}
                  onCancel={onCancelClick}
                  onDeclineOffer={onDeclineOffer}
                  onConfirm={onConfirm}
                  timeLeft={formatTime(timeLeft || 0)}
                  setShowConfirm={setShowConfirm}
                />
              )}
              {(transaction.paymentMethod === 'MC/VISA/MIR P2P RUB' ||
                transaction.paymentMethod === 'P2P MATCHING RUB' ||
                transaction.paymentMethod === 'MC/VISA P2P KZT' ||
                transaction.paymentMethod === 'MC/VISA P2P KRW') && (
                <P2PPage
                  transaction={transaction}
                  ticketId={uuid || ''}
                  onCancel={onCancelClick}
                  onDeclineOffer={onDeclineOffer}
                  onConfirm={onConfirm}
                  timeLeft={formatTime(timeLeft || 0)}
                  setShowConfirm={setShowConfirm}
                />
              )}
              {/* CPAYMENT-1637: у QR-реквизита нет номера — страницу выбираем по типу
                  реквизита, а не по методу: так новый QR-метод не требует правки списка. */}
              {transaction.requisite?.type === 'qr_code' && (
                <QrPage
                  transaction={transaction}
                  ticketId={uuid || ''}
                  onCancel={onCancelClick}
                  onDeclineOffer={onDeclineOffer}
                  onConfirm={onConfirm}
                  timeLeft={formatTime(timeLeft || 0)}
                  setShowConfirm={setShowConfirm}
                />
              )}
              {transaction.paymentMethod === 'A2A RUB' && (
                <A2APage
                  transaction={transaction}
                  ticketId={uuid || ''}
                  onCancel={onCancelClick}
                  onDeclineOffer={onDeclineOffer}
                  onConfirm={onConfirm}
                  timeLeft={formatTime(timeLeft || 0)}
                  setShowConfirm={setShowConfirm}
                />
              )}
            </>
          )}
        </WrapperDiv>
      )}
      {leaveChoice && (
        <LeaveChoiceModal
          onLeave={onLeave}
          onCancelTicket={onCancel}
          onStay={() => setLeaveChoice(false)}
        />
      )}
      {/* CPAYMENT-1731: попап предупреждения не должен наслаиваться на выбор выхода — клиенту,
          который уже открыл диалог ухода, важнее ответить на него. */}
      {lowTimeWarning && !leaveChoice && onExtendTime && onDismissLowTime && (
        <LowTimeModal onExtend={onExtendTime} onDismiss={onDismissLowTime} />
      )}
    </>
  );
};

export default DefaultTemplate;
